import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { aboutInfo, teamMembers } from '@/mocks/aboutData';
import { getOrganizationSchema, getWebPageSchema, getBreadcrumbSchema } from '@/components/base/SchemaOrg';

const tintMap: Record<number, string> = {
  0: 'bg-primary-50',
  1: 'bg-accent-50',
  2: 'bg-secondary-50',
  3: 'bg-accent-100',
};

export default function AboutPage() {
  const pageTitle = '关于我们 - 51吃瓜app | 品牌介绍与联系方式';
  const pageDesc = '了解51吃瓜app的品牌故事、发展历程、团队介绍与联系方式。国内领先的娱乐八卦资讯聚合平台，致力于为全国吃瓜群众提供最新最热的娱乐资讯。';
  const pageUrl = 'http://shlvtian.cn/about';

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(getOrganizationSchema()) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(getWebPageSchema(pageTitle, pageDesc, pageUrl)),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(
            getBreadcrumbSchema([
              { name: '首页', url: 'http://shlvtian.cn/' },
              { name: '关于我们', url: pageUrl },
            ])
          ),
        }}
      />

      <Navbar />

      <main className="pt-16">
        <Breadcrumb items={[{ label: '首页', path: '/' }, { label: '关于我们' }]} />

        <section className="w-full px-4 md:px-6 py-10 md:py-16 bg-background-100/50">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-xs text-foreground-400 uppercase tracking-wider mb-4">About Us</p>
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-6 leading-tight">
              {aboutInfo.brandName}
              <br />
              <span className="text-primary-500">{aboutInfo.brandIntro}</span>
            </h1>
            <p className="text-base md:text-lg text-foreground-700 leading-relaxed font-heading max-w-2xl mx-auto">
              {aboutInfo.description}
            </p>
            <p className="text-sm text-foreground-500 mt-4 italic">
              &ldquo;{aboutInfo.mission}&rdquo;
            </p>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-16 bg-background-50">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-10">
              发展历程
            </h2>
            <div className="relative">
              <div className="absolute left-[19px] md:left-1/2 top-0 bottom-0 w-px bg-background-200 md:-translate-x-px"></div>
              <div className="space-y-8">
                {aboutInfo.history.map((item, index) => (
                  <div
                    key={index}
                    className={`relative flex items-start gap-6 ${
                      index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    <div className="md:w-1/2 flex-shrink-0 flex items-center gap-6">
                      <div className="relative z-10 w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center flex-shrink-0">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-check-line text-background-50 text-sm"></i>
                        </div>
                      </div>
                      <div
                        className={`${
                          index % 2 === 0
                            ? 'md:text-right md:pr-10'
                            : 'md:text-left md:pl-10 md:ml-auto'
                        }`}
                      >
                        <span className="text-sm font-bold text-primary-500">{item.year}</span>
                        <p className="text-sm text-foreground-700 mt-1">{item.event}</p>
                      </div>
                    </div>
                    <div className="hidden md:block md:w-1/2"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="mb-8">
              <p className="text-xs font-heading text-foreground-500 uppercase tracking-wider mb-2">Our Team</p>
              <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 leading-tight">
                核心团队
                <br />
                <span className="italic font-heading">吃瓜背后的力量</span>
              </h2>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {teamMembers.map((member, index) => (
                <div key={index} className="group">
                  <div className={`rounded-2xl overflow-hidden ${tintMap[index] || 'bg-background-100'}`}>
                    <div className="relative w-full aspect-[3/4] overflow-hidden">
                      <img
                        src={member.avatar}
                        alt={member.name}
                        title={`${member.name} - ${member.role} - 51吃瓜app`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                  </div>
                  <div className="p-3 md:p-4 bg-background-50">
                    <h3 className="text-sm md:text-base font-medium text-foreground-900">
                      {member.name}
                    </h3>
                    <p className="text-xs text-primary-500 mt-0.5">{member.role}</p>
                    <p className="text-xs text-foreground-500 mt-1 line-clamp-1">{member.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-16 bg-background-100/50">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-10">
              <span className="text-foreground-400 font-normal">[</span>
              品牌荣誉
              <span className="text-foreground-400 font-normal">]</span>
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {aboutInfo.honors.map((honor, index) => (
                <div key={index} className="text-center p-5 bg-background-50 rounded-xl">
                  <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-primary-50 flex items-center justify-center">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className={`${honor.icon} text-primary-500 text-lg`}></i>
                    </div>
                  </div>
                  <h3 className="text-sm font-medium text-foreground-900 mb-1">{honor.title}</h3>
                  <p className="text-xs text-foreground-500">{honor.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-16 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-10">
              <span className="text-foreground-400 font-normal">[</span>
              媒体口碑
              <span className="text-foreground-400 font-normal">]</span>
            </h2>

            <div className="relative">
              <div className="bg-background-100/50 rounded-2xl p-6 md:p-10">
                <div className="flex items-center gap-2 mb-6">
                  <div className="px-3 py-1 bg-foreground-900 text-background-50 text-xs rounded-full flex items-center gap-1.5">
                    <div className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-star-fill text-accent-400 text-[10px]"></i>
                    </div>
                    {aboutInfo.mediaReviews[0].rating}
                  </div>
                </div>

                <blockquote className="text-base md:text-lg text-foreground-800 leading-relaxed font-heading mb-6">
                  &ldquo;{aboutInfo.mediaReviews[0].quote}&rdquo;
                </blockquote>

                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full overflow-hidden bg-background-200">
                    <img
                      src={aboutInfo.mediaReviews[0].avatar}
                      alt={aboutInfo.mediaReviews[0].reviewer}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">
                      {aboutInfo.mediaReviews[0].reviewer}
                    </p>
                    <p className="text-xs text-foreground-500">
                      {aboutInfo.mediaReviews[0].role}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-4">
                <button className="w-9 h-9 rounded-lg border border-background-200 bg-background-50 flex items-center justify-center hover:bg-background-100 transition-colors cursor-pointer">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-left-s-line text-foreground-600"></i>
                  </div>
                </button>
                <button className="w-9 h-9 rounded-lg bg-foreground-900 flex items-center justify-center hover:bg-foreground-800 transition-colors cursor-pointer">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-s-line text-background-50"></i>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-16 bg-background-100/50">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-10">
              联系我们
            </h2>
            <div className="bg-background-50 rounded-2xl p-6 md:p-10">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary-50 flex items-center justify-center flex-shrink-0">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-mail-line text-primary-500"></i>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400 mb-0.5">邮箱</p>
                    <p className="text-sm font-medium text-foreground-900">{aboutInfo.contact.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary-50 flex items-center justify-center flex-shrink-0">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-phone-line text-primary-500"></i>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400 mb-0.5">电话</p>
                    <p className="text-sm font-medium text-foreground-900">{aboutInfo.contact.phone}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary-50 flex items-center justify-center flex-shrink-0">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-map-pin-line text-primary-500"></i>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400 mb-0.5">地址</p>
                    <p className="text-sm font-medium text-foreground-900">{aboutInfo.contact.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary-50 flex items-center justify-center flex-shrink-0">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-wechat-line text-primary-500"></i>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400 mb-0.5">微信</p>
                    <p className="text-sm font-medium text-foreground-900">{aboutInfo.contact.wechat}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}